'use client';

import { useRouter } from 'next/navigation';
import { MouseEvent, useCallback, useState, useTransition } from 'react';

const opensOutsideCurrentTab = (event: MouseEvent) =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

/**
 * Pending state for an App Router navigation: `router.push` inside a transition
 * stays pending until the target server component is rendered, which is exactly
 * how long the user waits on dynamic pages.
 */
export const useNavigationTransition = () => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [href, setHref] = useState<string | null>(null);

  const navigate = useCallback(
    (href: string) => {
      setHref(href);
      startTransition(() => router.push(href));
    },
    [router],
  );

  const getLinkProps = useCallback(
    (href: string) => ({
      href,
      onClick: (event: MouseEvent<HTMLAnchorElement>) => {
        if (opensOutsideCurrentTab(event)) return;

        event.preventDefault();
        navigate(href);
      },
    }),
    [navigate],
  );

  return {
    navigate,
    getLinkProps,
    isNavigating: isPending,
    pendingHref: isPending ? href : null,
  };
};
